import React, { useEffect, useState, forwardRef, useImperativeHandle } from 'react';
import { useTranslation } from 'react-i18next';
import './PurchaseOrders.css';
import { API_CONFIG, getApiUrl, apiFetch } from '../../config/api';
import { formatAppDate } from '../../utils/dateUtils';
import { PO_STATUSES, PO_STATUS_CLASSES, poStatusLabel } from './poStatus';

const PAGE_SIZE = 1000;

const PurchaseOrdersList = forwardRef(({ onSelectPurchaseOrder, selectedPurchaseOrderId, onNew }, ref) => {
  const { t } = useTranslation('procurement');
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useImperativeHandle(ref, () => ({
    fetchPurchaseOrders
  }));

  useEffect(() => {
    fetchPurchaseOrders();
  }, []);

  // The API answers pages of up to 1000 POs: ask until a short page so none is left out (#366)
  const fetchPurchaseOrders = async () => {
    setLoading(true);
    try {
      const all = [];
      let response;
      for (let skip = 0; ; skip += PAGE_SIZE) {
        response = await apiFetch(getApiUrl(`/api/purchase-orders?limit=${PAGE_SIZE}&skip=${skip}`));
        if (!response.ok) break;
        const data = await response.json();
        const page = Array.isArray(data) ? data : data.purchase_orders || [];
        all.push(...page);
        if (page.length < PAGE_SIZE) break;
      }
      if (response.ok) {
        setPurchaseOrders(all);
        setError(null);
      } else {
        setError(t('purchaseOrders.error.fetchFailed'));
      }
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      setError(t('purchaseOrders.error.loadingError'));
    } finally {
      setLoading(false);
    }
  };

  const filteredPurchaseOrders = purchaseOrders.filter(po => {
    const matchesSearch = (po.po_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          po.supplier_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          po.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          po.reference?.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesStatus = statusFilter === 'all' || po.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status) => (
    <span className={`status-badge ${PO_STATUS_CLASSES[status] || 'status-inactive'}`}>{poStatusLabel(t, status)}</span>
  );

  const formatDate = (dateString) => {
    if (!dateString) return t('purchaseOrders.defaults.notApplicable');
    return formatAppDate(dateString);
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return `$${parseFloat(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleNewClick = () => {
    if (onNew) {
      onNew();
    }
  };

  if (loading && purchaseOrders.length === 0) {
    return <div className="list-container"><div className="loading">{t('purchaseOrders.loading')}</div></div>;
  }

  return (
    <div className="list-container">
      <div className="list-header">
        <h2>{t('purchaseOrders.title')}</h2>
        <div className="header-actions">
          <button className="btn-primary" onClick={handleNewClick}>
            <i className="icon">+</i> {t('purchaseOrders.newPO')}
          </button>
        </div>
      </div>

      <div className="list-controls">
        <div className="search-box">
          <input
            type="text"
            placeholder={t('purchaseOrders.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">{t('purchaseOrders.filters.allStatus')}</option>
            {PO_STATUSES.map(status => (
              <option key={status} value={status}>{poStatusLabel(t, status)}</option>
            ))}
          </select>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="list-table">
        <table>
          <thead>
            <tr>
              <th>{t('purchaseOrders.tableHeaders.poNumber')}</th>
              <th>{t('purchaseOrders.tableHeaders.supplier')}</th>
              <th>{t('purchaseOrders.tableHeaders.orderDate')}</th>
              <th>{t('purchaseOrders.tableHeaders.expectedDelivery')}</th>
              <th>{t('purchaseOrders.tableHeaders.totalAmount')}</th>
              <th>{t('purchaseOrders.tableHeaders.status')}</th>
            </tr>
          </thead>
          <tbody>
            {filteredPurchaseOrders.length === 0 ? (
              <tr>
                <td colSpan="6" className="no-data">
                  {searchTerm ? t('purchaseOrders.noData.searching') : t('purchaseOrders.noData.empty')}
                </td>
              </tr>
            ) : (
              filteredPurchaseOrders.map(po => (
                <tr
                  key={po.po_id}
                  onClick={() => onSelectPurchaseOrder(po.po_id)}
                  className={selectedPurchaseOrderId === po.po_id ? 'selected' : ''}
                >
                  <td className="po-number">
                    <strong>{po.po_number || t('purchaseOrders.defaults.notApplicable')}</strong>
                  </td>
                  <td className="supplier-info">
                    <div className="supplier-name">
                      <strong>{po.supplier_name || t('purchaseOrders.defaults.unknown')}</strong>
                    </div>
                    {po.supplier_contact && <div className="text-secondary">{po.supplier_contact}</div>}
                  </td>
                  <td>
                    {formatDate(po.order_date)}
                  </td>
                  <td>
                    {formatDate(po.expected_delivery)}
                  </td>
                  <td className="text-right">
                    {formatCurrency(po.total_amount)}
                  </td>
                  <td>
                    {getStatusBadge(po.status)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="list-footer">
        <div className="record-count">
          {t('purchaseOrders.recordCount', { filtered: filteredPurchaseOrders.length, total: purchaseOrders.length })}
        </div>
      </div>
    </div>
  );
});

PurchaseOrdersList.displayName = 'PurchaseOrdersList';

export default PurchaseOrdersList;